import {
  dialogsListApi,
  generatedApi,
  type DialogShortData,
  type GetListDialogsDialogListPostApiArg,
} from '@shared/api';
import type { RootState } from '@shared/redux';

export const getDialogListOrganizationId = (args: GetListDialogsDialogListPostApiArg) => {
  const organizationId = args.dialogListFilters?.organization_id;
  return typeof organizationId === 'number' && Number.isFinite(organizationId)
    ? organizationId
    : null;
};

export const getCachedDialogFromList = (
  state: RootState,
  args: GetListDialogsDialogListPostApiArg,
  clientId: number,
) => {
  const data = dialogsListApi.endpoints.getListDialogsDialogListPostQuery.select(args)(
    state as any,
  );

  return data.data?.items?.find((dialog) => dialog.client_id === clientId) ?? null;
};

export const getCachedDialogOrganizations = (state: RootState, clientId: number) => {
  const dialogByOrganizationId = new Map<number, DialogShortData>();
  const cachedArgs = dialogsListApi.util.selectCachedArgsForQuery(
    state,
    'getListDialogsDialogListPostQuery',
  );

  cachedArgs?.forEach((args) => {
    const organizationId = getDialogListOrganizationId(args);
    if (!organizationId || dialogByOrganizationId.has(organizationId)) return;

    const dialog = getCachedDialogFromList(state, args, clientId);
    if (dialog) {
      dialogByOrganizationId.set(organizationId, dialog);
    }
  });

  return dialogByOrganizationId;
};

export const getCachedChannelOrganizationIds = (state: RootState, channelId: number) => {
  const organizationIds = new Set<number>();
  const cachedArgs = generatedApi.util.selectCachedArgsForQuery(
    state,
    'getChannelListChannelListGet',
  );

  cachedArgs?.forEach((args) => {
    const data = generatedApi.endpoints.getChannelListChannelListGet.select(args)(state as any);
    const hasChannel = data.data?.some((channel) => channel.id === channelId);

    if (hasChannel) {
      organizationIds.add(args.organizationId);
    }
  });

  return organizationIds;
};

export const getCachedChannelType = (state: RootState, channelId: number) => {
  const cachedArgs = generatedApi.util.selectCachedArgsForQuery(
    state,
    'getChannelListChannelListGet',
  );

  for (const args of cachedArgs ?? []) {
    const data = generatedApi.endpoints.getChannelListChannelListGet.select(args)(state as any);
    const channel = data.data?.find((item) => item.id === channelId);

    if (channel) {
      return channel.type;
    }
  }

  return null;
};

export const shouldUpdateDialogListByChannel = ({
  args,
  channelId,
  hasExistingDialog,
  dialogOrganizationIds,
  channelOrganizationIds,
  eventOrganizationId,
}: {
  args: GetListDialogsDialogListPostApiArg;
  channelId: number;
  hasExistingDialog: boolean;
  dialogOrganizationIds: Set<number>;
  channelOrganizationIds: Set<number>;
  eventOrganizationId?: number | null;
}) => {
  const filteredChannels = args.dialogListFilters?.channel_ids;
  const organizationId = getDialogListOrganizationId(args);

  if (eventOrganizationId && organizationId !== eventOrganizationId) {
    return false;
  }

  if (
    Array.isArray(filteredChannels) &&
    filteredChannels.length &&
    !filteredChannels.includes(channelId)
  ) {
    return false;
  }

  if (hasExistingDialog) return true;

  if (!organizationId) return false;

  return (
    dialogOrganizationIds.has(organizationId) ||
    channelOrganizationIds.has(organizationId) ||
    (Array.isArray(filteredChannels) && filteredChannels.includes(channelId))
  );
};
